// One project's documents on this computer: rebuilt from the offline store first (so it reads with no network), then
// brought up to date over the relay's socket, which stays open for other devices' changes. Every entry that arrives
// is stored sealed before it's applied, so what's on screen is always what a reload offline would show.
//
// Writing (W4a): `edit` applies `ProjectEditor` calls to the replica's documents and hands what changed to the outbox,
// which batches, seals, stores and sends them (Web/client/src/net/outbox.ts). Batches kept from an earlier session are
// applied to the replica when it's rebuilt, so what was typed offline is there after a reload; each one the relay
// acknowledges is kept as the entry it numbered. Each save logs the words typed, per fragment, against its last saved
// count, as the Mac's EditorController does.
//
// Epochs (W4e): a document the Mac compacted arrives as a newer epoch's snapshot. The replica holds it until the
// outbox has settled the document (anything of this device's that may be on the relay acknowledged, so it counts as
// synced), then adopts it: the old copy archived here (sealed, `archive:` in the offline store), the relay's copy of the
// old history absorbed as the Swift absorbs an older epoch (recording `absorbedHeads`), this device's unsynced edits
// rebased; the outbox drops its unsent old-epoch batches for it (`replace`) and sends those changes as a batch of
// the new epoch. Batches of an older epoch that still arrive are absorbed as the Swift absorbs an older epoch. An open
// editor takes the new text as another device's change. When something of this device's couldn't be carried over, `epochNotice` says so.
//
// Shared projects (W9a, PROTOCOL.md §10): `id` is the project's address (its id, or `<owner>/<id>` for one shared with
// this account), so the cache, outbox and archives of an owner's project and a member's own never meet. A commenter's
// project isn't `writable` (no prose, fields or structure) but `canComment`. When the project's key changes (a rotation:
// the relay refuses an append with `keyChanged`, or an entry arrives that no key here opens), `checkKey` asks the app for
// the key now (`deps.fetchKey`) and `rekey` takes it: the outbox's kept items and the cache re-sealed with it, the replica
// rebuilt, the old key kept in memory for entries sealed before. The relay closing the socket with 4004 means this
// account was removed from the project (`deps.removed`).
import * as A from '@automerge/automerge';
import type { Api } from '@writing-suite/client/net/api.ts';
import { Outbox, type OutboxItem, type OutboxStatus, type OutboxStore, type Timers } from '@writing-suite/client/net/outbox.ts';
import { Replica } from '@writing-suite/client/net/replica.ts';
import { SyncConnection, type Loaded, type PresenceHeader } from '@writing-suite/client/net/sync.ts';
import type { WireEntry } from '@writing-suite/client/shared/wire.ts';
import { editProject, open as openDocs, ROOT, sha256Hex, SwiftReader, wordCount, type FragmentKind, type Project, type ProjectEditor } from '@writing-suite/core';
import { hasMembers, recordMemberIn } from '@writing-suite/core/members';
import { attachmentDoc, seal } from '@writing-suite/client/shared/crypto.ts';
import { fromHex } from '@writing-suite/client/shared/b64.ts';
import { open as openSealed } from '@writing-suite/client/shared/crypto.ts';
import type { ArchivedDoc, EpochEvent } from '@writing-suite/client/net/replica.ts';
import { COMPACT_AFTER, type CachedEntry, type OfflineStore } from './offline.ts';
import { perfMark, perfMeasure } from '../shell/perf.ts';

export type Connection = 'offline' | 'connecting' | 'live' | 'closed';

export interface ProjectState {
  status: 'opening' | 'ready' | 'empty' | 'failed';
  project: Project | null;
  /** Bumped whenever `project` is rebuilt. */
  version: number;
  connection: Connection;
  error: string | null;
  timings: { cacheMs: number | null; networkMs: number | null; cached: number; fetched: number };
  /** This device's edits on their way to the relay (null while reading only). */
  save: OutboxStatus | null;
  /** A document compacted on another device, and something of this device's that couldn't be carried into it. */
  epochNotice: { doc: string; message: string } | null;
}

export interface ProjectDeps {
  api: Api;
  store: OfflineStore;
  online(): boolean;
  /** The relay closed the socket because this device was signed out. */
  sessionEnded(): void;
  /** Builds the core's project (injectable for tests). */
  build?: (docs: Map<string, A.Doc<unknown>>) => Project;
  /** Seal for the local compaction and the outbox (the client's AES-GCM; injectable for tests). */
  seal?: (key: Uint8Array, bytes: Uint8Array) => Promise<Uint8Array>;
  /** This device's id (word logs) and the writer's name (comments, checkpoints). */
  device?: string;
  author?: string;
  /** Where batches wait (default: the offline store's). */
  outboxStore?: OutboxStore;
  timers?: Timers;
  /** The outbox's idle pause (2 s). */
  idleMs?: number;
  /** The relay connection (tests pass a fake; by default the project's WebSocket). */
  connection?: (projectId: string) => ProjectConnection;
  /** This account's role in the project (W9a): a commenter's edits are comments and suggestions only. Default owner. */
  role?: 'owner' | 'cowriter' | 'commenter';
  /** The project's key as the relay has it now (after a rotation), or null when it can't be had. */
  fetchKey?: () => Promise<Uint8Array | null>;
  /** The relay closed the socket because this account is no longer a member (4004). */
  removed?: () => void;
  /** This account's relay id: in a shared project, each session records its actor under it (W9c, `members`). */
  memberId?: string;
}

/** What a live project needs of its relay connection (`SyncConnection`). */
export type ProjectConnection = Pick<SyncConnection, 'connect' | 'hello' | 'pack' | 'append' | 'ping' | 'close' | 'closed' | 'onNew' | 'onClose'>;

/** Entries in replay order: snapshots before batches, each in sequence (as `Replica.load` applies a welcome). */
export function replayOrder<T extends { kind: string; seq: number }>(entries: T[]): T[] {
  const bySeq = [...entries].sort((a, b) => a.seq - b.seq);
  return [...bySeq.filter((e) => e.kind === 'snapshot'), ...bySeq.filter((e) => e.kind !== 'snapshot')];
}

export function docsOf(replica: Replica): Map<string, A.Doc<unknown>> {
  return new Map([...replica.docs].map(([name, d]) => [name, d.doc as A.Doc<unknown>]));
}

/** A document as it was synced (the relay's entries and this device's acknowledged batches only): what the cache keeps. */
function syncedBytes(d: { doc: A.Doc<unknown>; synced: string[] }): Uint8Array | null {
  if (!d.synced.length) return null; // nothing of it has reached the relay: it waits in the outbox
  const heads = [...A.getHeads(d.doc)].sort();
  const synced = [...d.synced].sort();
  if (heads.length === synced.length && heads.every((h, i) => h === synced[i])) return A.save(d.doc);
  try {
    return A.getBackend(d.doc).fork(undefined, synced).save();
  } catch {
    return null;
  }
}

const FRAGMENT = 'fragment.';

const sameBytes = (a: Uint8Array, b: Uint8Array) => a.length === b.length && a.every((x, i) => x === b[i]);

const wire = (e: CachedEntry): WireEntry => ({ seq: e.seq, doc: e.doc, kind: e.kind, epoch: e.epoch, lineage: e.lineage, heads: e.heads, size: e.bytes.length });

/** A replica rebuilt from cached sealed entries (and archived older histories). Epoch work waits for `settleEpochs`. */
export async function replicaFrom(key: Uint8Array, entries: CachedEntry[], replica = new Replica(key)): Promise<Replica> {
  replica.deferEpochWork = true;
  for (const e of replayOrder(entries)) {
    try {
      await replica.apply(wire(e), e.bytes);
    } catch {
      replica.setAside++; // unreadable (another key's, or damaged): skipped, as the relay's copy will replace it
    }
  }
  return replica;
}

/**
 * The cache compacted: one sealed snapshot per document the replica holds (numbered with its newest entry's seq),
 * plus any entries for documents it doesn't. Only for the local cache: nothing is sent.
 */
export async function compactEntries(
  replica: Replica,
  key: Uint8Array,
  entries: CachedEntry[],
  sealWith: (key: Uint8Array, bytes: Uint8Array) => Promise<Uint8Array>,
): Promise<CachedEntry[]> {
  const newest = new Map<string, number>();
  for (const e of entries) newest.set(e.doc, Math.max(newest.get(e.doc) ?? 0, e.seq));
  const out: CachedEntry[] = entries.filter((e) => !replica.docs.has(e.doc));
  for (const [doc, d] of replica.docs) {
    const seq = newest.get(doc);
    const bytes = seq === undefined ? null : syncedBytes(d);
    if (seq === undefined || !bytes) {
      out.push(...entries.filter((e) => e.doc === doc));
      continue;
    }
    // Only what the relay has: this device's unacknowledged edits stay in the outbox, not in the cache.
    out.push({ seq, doc, kind: 'snapshot', epoch: d.epoch, lineage: d.lineage, bytes: await sealWith(key, bytes) });
  }
  return out.sort((a, b) => a.seq - b.seq);
}

type Listener = (s: ProjectState) => void;

/** Another device's change to one document: its name, the heads it had before (null if it was new), and the document now. */
export type RemoteListener = (doc: string, beforeHeads: A.Heads | null, after: A.Doc<unknown>) => void;

const RETRY_MS = [1000, 2000, 5000, 10_000, 30_000];
const ATTACHMENT_CACHE_BYTES = 64 * 1024 * 1024;
const TYPING_PAUSE_MS = 250;
const TYPING_MAX_MS = 1000;

export class LiveProject {
  private listeners = new Set<Listener>();
  private replica: Replica | null = null;
  private conn: ProjectConnection | null = null;
  private cursor = 0;
  /** The relay's sequence this replica has merged to (for a side connection, so the log isn't replayed to it). */
  get mergedCursor(): number { return this.cursor; }
  /** The attachment entries this device has cached (`attachment.<hmac>` → newest seq): files, not documents. */
  private readonly attachmentSeqs = new Map<string, number>();
  /** Opened attachments, by SHA-256, up to `ATTACHMENT_CACHE_BYTES` in all. */
  private readonly openedAttachments = new Map<string, Uint8Array>();
  private openedAttachmentBytes = 0;
  private closed = false;
  private retries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private rebuildTimer: ReturnType<typeof setTimeout> | null = null;
  private queue: Promise<void> = Promise.resolve();
  private readonly onOnline = () => void this.connect();
  private readonly onOffline = () => {
    this.update({ connection: 'offline' });
    // The browser says it's offline: stop using the socket (edits wait in the outbox); `online` reconnects.
    const conn = this.conn;
    if (!conn) return;
    this.conn = null;
    conn.onClose = null;
    conn.close();
    this.outbox?.connected(null);
    if (this.pingTimer) clearInterval(this.pingTimer);
  };
  /** The last write of the Library's figures (awaited before `open` resolves). */
  private infoSaved: Promise<unknown> = Promise.resolve();
  private lastInfo = '';
  /** Entries stored past the cursor out of order (this device's own, acknowledged before others' arrived). */
  private above = new Set<number>();
  private limits: Loaded['limits'] | null = null;
  /** This device's edits on their way to the relay (the open project only). */
  outbox: Outbox | null = null;
  /** Word counts at the last save, per fragment typed in (EditorController's `savedWords`), and those typed in since. */
  private savedWords = new Map<string, number>();
  private typed = new Set<string>();
  private remoteListeners = new Set<RemoteListener>();
  /** This device's own changes made outside the editor (Replace All, a suggestion accepted from the list…). */
  private localListeners = new Set<RemoteListener>();
  /** Each save's words per fragment typed in (`wordsSaved`: sessions and sprints count them). */
  private savedListeners = new Set<(fragmentId: string, before: number, after: number) => void>();
  /** Other members' presence frames (W9b, PROTOCOL.md §10.12; store/presence.ts reads them). */
  private presenceListeners = new Set<(header: PresenceHeader, body: Uint8Array) => void>();
  state: ProjectState = {
    status: 'opening',
    project: null,
    version: 0,
    connection: 'closed',
    error: null,
    timings: { cacheMs: null, networkMs: null, cached: 0, fetched: 0 },
    save: null,
    epochNotice: null,
  };

  /** Keys the project had before (a rotation): what they sealed still opens here, in this page's life. */
  private previousKeys: Uint8Array[] = [];
  /** The key the project's entries are sealed with now (`rekey` replaces it). */
  key: Uint8Array;

  /** The actor this session writes a shared project with (W9c): fresh for every session, so no two tabs or reloads
   *  ever share one, and recorded under this account in the project's `members` on the first write. */
  readonly sessionActor = crypto.randomUUID().replaceAll('-', '').toLowerCase();
  /** Actors (and the name) already recorded in `members` this session. */
  private recordedActors = new Set<string>();
  private recordedName: string | null = null;
  /** The owner shared the project this session (Share Project…): its writes are attributed from now on. */
  private sharedHere = false;
  private attributingFor: { doc: A.Doc<unknown> | null; value: boolean } = { doc: null, value: false };

  constructor(
    /** The project's address: its id, or `<owner>/<id>` for a project shared with this account. */
    readonly id: string,
    key: Uint8Array,
    private readonly deps: ProjectDeps,
    /** Keep the socket for other devices' changes (the open project); false closes it once caught up (the Library). */
    readonly live = true,
  ) {
    this.key = key;
  }

  /** This account's relay id (`members`' key for this account), when known. */
  get memberId(): string | null {
    return this.deps.memberId ?? null;
  }

  /** This account's role in the project. */
  get role(): 'owner' | 'cowriter' | 'commenter' {
    return this.deps.role ?? 'owner';
  }

  /** The save's status changes with every keystroke: only its own listeners hear each change (the note in the page's
   *  head); the project's hear when it connects, disconnects or is refused. */
  private updateSave(save: OutboxStatus) {
    const was = this.state.save;
    this.state = { ...this.state, save };
    for (const fn of this.saveListeners) fn();
    if (!was || was.connected !== save.connected || was.refused.length !== save.refused.length) for (const fn of this.listeners) fn(this.state);
  }

  private saveListeners = new Set<() => void>();

  /** Calls `fn` whenever the save's status changes (read it from `state.save`). */
  onSave(fn: () => void): () => void {
    this.saveListeners.add(fn);
    return () => this.saveListeners.delete(fn);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private update(change: Partial<ProjectState>) {
    this.state = { ...this.state, ...change };
    for (const fn of this.listeners) fn(this.state);
  }

  /** Reads the cache, then catches up with the relay when online. Resolves once both have been tried. */
  async open(): Promise<void> {
    const t0 = performance.now();
    const cached = await this.deps.store.entries(this.id);
    this.cursor = await this.deps.store.cursor(this.id);
    this.noteAttachments(cached);
    this.replica = await replicaFrom(this.key, cached, await this.newReplica());
    // Batches this device made that the relay hasn't acknowledged: part of the text here, and sent when connected.
    const outboxStore = this.deps.outboxStore ?? this.deps.store.outbox(this.id);
    await this.applyPending(await Outbox.pending(outboxStore).catch(() => []));
    const deferred = this.replica.settleEpochs();
    if (this.live && !this.closed) {
      this.outbox = new Outbox({
        key: this.key,
        store: outboxStore,
        source: {
          get: (name) => this.replica?.docs.get(name) as { doc: A.Doc<unknown>; epoch: number; lineage: string } | undefined,
          names: () => this.replica?.docs.keys() ?? [],
          cursor: () => this.cursor,
        },
        seal: this.deps.seal,
        timers: this.deps.timers,
        idleMs: this.deps.idleMs,
        beforeBatch: (names) => this.logWords(names),
        onAcked: (item, seq) => this.acked(item, seq),
        onStatus: (save) => this.updateSave(save),
        onStale: () => this.refetch(),
        onKeyChanged: () => void this.checkKey(),
      });
      await this.outbox.start(cached.map((e) => ({ doc: e.doc, kind: e.kind, seq: e.seq, size: e.bytes.length })));
    }
    // What the cache's older-epoch entries brought (absorbed now, and sent), then this device's own older-epoch batches
    // from an earlier session, rebased once they've settled.
    this.handleEpochs(deferred);
    this.enqueue(() => this.settleArchived());
    this.rebuild();
    this.update({ timings: { ...this.state.timings, cacheMs: Math.round(performance.now() - t0), cached: cached.length } });
    if (this.live && typeof window !== 'undefined') {
      window.addEventListener('online', this.onOnline);
      window.addEventListener('offline', this.onOffline);
    }
    await this.connect();
    if (this.state.status === 'opening') this.update({ status: 'empty' });
    await this.infoSaved;
    this.compactIfLarge(cached.length);
  }

  close() {
    this.closed = true;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
      window.removeEventListener('offline', this.onOffline);
    }
    for (const t of [this.retryTimer, this.rebuildTimer]) if (t) clearTimeout(t);
    if (this.pingTimer) clearInterval(this.pingTimer);
    // Anything typed is batched and stored; it goes the next time the project opens.
    const outbox = this.outbox;
    if (outbox) {
      outbox.connected(null);
      void outbox.flush().finally(() => outbox.stop());
    }
    this.conn?.close();
    this.conn = null;
    this.listeners.clear();
    this.remoteListeners.clear();
    this.localListeners.clear();
    this.savedListeners.clear();
    this.saveListeners.clear();
    this.presenceListeners.clear();
  }

  /** Connects, catches up from the cursor, and (when live) keeps listening. Quietly stays offline on failure. */
  async connect(): Promise<void> {
    if (this.closed || (this.conn && !this.conn.closed)) return;
    if (!this.deps.online()) return this.update({ connection: 'offline' });
    const t0 = performance.now();
    this.update({ connection: 'connecting' });
    const conn = this.deps.connection?.(this.id) ?? new SyncConnection(this.deps.api, this.id);
    const early: { entry: WireEntry; bytes: Uint8Array | null }[] = [];
    let caughtUp = false;
    conn.onNew = (entry, bytes) => {
      if (!caughtUp) early.push({ entry, bytes });
      else this.enqueue(() => this.receive(conn, entry, bytes));
    };
    conn.onClose = (code) => this.closedBy(conn, code);
    // Presence (W9b): asked for in `hello` by the open project's socket, and handed to store/presence.ts.
    const withPresence = conn as Partial<Pick<SyncConnection, 'presence' | 'onPresence'>>;
    withPresence.presence = this.live;
    withPresence.onPresence = (header, body) => {
      if (this.conn === conn) for (const fn of this.presenceListeners) fn(header, body);
    };
    try {
      await conn.connect();
      this.conn = conn;
      let loaded = await conn.hello(this.cursor);
      if (loaded.reset) {
        // The relay knows less than this cache claims (a restored relay): start again from nothing.
        await this.deps.store.replace(this.id, [], 0);
        this.cursor = 0;
        this.above.clear();
        this.replica = await this.newReplica(false);
        await this.applyPending(this.outbox ? await Outbox.pending(this.deps.outboxStore ?? this.deps.store.outbox(this.id)).catch(() => []) : []);
        this.handleEpochs(this.replica.settleEpochs());
        loaded = await conn.hello(0);
      }
      const fetched = await this.take(conn, loaded);
      caughtUp = true;
      this.retries = 0;
      for (const n of early) if (n.entry.seq > this.cursor) this.enqueue(() => this.receive(conn, n.entry, n.bytes));
      await this.queue;
      this.update({
        connection: this.live ? 'live' : 'closed',
        timings: { ...this.state.timings, networkMs: Math.round(performance.now() - t0), fetched: this.state.timings.fetched + fetched },
      });
      if (!this.live) {
        conn.close();
        this.conn = null;
      } else {
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = setInterval(() => conn.ping(), 30_000);
        // Caught up: the outbox's snapshots can name this cursor as their base, and what's waiting goes now.
        const limits = this.limits ?? loaded.limits;
        this.outbox?.connected({ append: (kind, fields, sealed) => conn.append(kind, fields, sealed, limits) });
        // Newer epochs that waited for this device's batches to settle, and older-epoch edits of its own to rebase.
        this.enqueue(() => this.settleArchived());
      }
    } catch (e) {
      conn.onClose = null;
      conn.close();
      this.outbox?.connected(null);
      if (this.conn === conn) this.conn = null;
      this.update({ connection: 'offline', error: this.state.project ? null : e instanceof Error ? e.message : String(e) });
      this.scheduleRetry();
    }
  }

  private closedBy(conn: ProjectConnection, code: number) {
    if (this.conn !== conn) return;
    this.conn = null;
    this.outbox?.connected(null);
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.closed) return;
    if (code === 4001) return this.deps.sessionEnded(); // this device was signed out
    if (code === 4004) {
      // Removed from the project (or left it): nothing more comes, and nothing more is sent.
      this.update({ connection: 'closed' });
      return this.deps.removed?.();
    }
    this.update({ connection: this.deps.online() ? 'connecting' : 'offline' });
    this.scheduleRetry();
  }

  private scheduleRetry() {
    if (this.closed || !this.live || this.retryTimer) return;
    const wait = RETRY_MS[Math.min(this.retries++, RETRY_MS.length - 1)];
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, wait);
  }

  private enqueue(work: () => Promise<void>) {
    this.queue = this.queue.then(work).catch(() => {});
  }

  /** A welcome's entries: blobs fetched, all stored sealed, then applied snapshots first. Returns how many. */
  private async take(conn: ProjectConnection, loaded: Loaded): Promise<number> {
    const blobs = await conn.pack(loaded.entries.flatMap((e) => (e.blob ? [e.blob] : [])));
    const entries: CachedEntry[] = [];
    for (const e of loaded.entries) {
      const bytes = e.blob ? blobs.get(e.blob) : loaded.inline.get(e.seq);
      if (bytes) entries.push({ seq: e.seq, doc: e.doc, kind: e.kind, epoch: e.epoch, lineage: e.lineage, heads: e.heads, bytes: bytes.slice() });
    }
    this.limits = loaded.limits;
    const cursor = Math.max(this.cursor, loaded.head);
    await this.deps.store.append(this.id, entries, cursor);
    this.noteAttachments(entries);
    this.cursor = cursor;
    for (const seq of [...this.above]) if (seq <= cursor) this.above.delete(seq);
    if (entries.length) {
      const replica = this.replica ?? (await this.newReplica(false));
      this.replica = replica;
      for (const e of replayOrder(entries)) await this.applyRemote(wire(e), e.bytes);
      this.rebuild();
    }
    return entries.length;
  }

  /** Another device's entry: stored, then applied; the cursor moves only over an unbroken run of seqs. */
  private async receive(conn: ProjectConnection, entry: WireEntry, inline: Uint8Array | null) {
    const bytes = inline ?? (entry.blob ? (await conn.pack([entry.blob])).get(entry.blob) : undefined);
    if (!bytes || !this.replica) return;
    this.advance(entry.seq);
    const cached: CachedEntry = { seq: entry.seq, doc: entry.doc, kind: entry.kind, epoch: entry.epoch, lineage: entry.lineage, heads: entry.heads, bytes: bytes.slice() };
    await this.deps.store.append(this.id, [cached], this.cursor);
    this.noteAttachments([cached]);
    await this.applyRemote(wire(cached), cached.bytes);
    this.scheduleRebuild();
  }

  /** The cursor moves over an unbroken run of stored seqs only (so a reload asks again for anything missed). */
  private advance(seq: number) {
    if (seq <= this.cursor) return;
    if (seq !== this.cursor + 1) {
      this.above.add(seq);
      return;
    }
    this.cursor = seq;
    while (this.above.delete(this.cursor + 1)) this.cursor += 1;
  }

  /** Another device's entry (or the relay's copy of this one's): applied, counted for the outbox, and the words it
   *  brought kept out of this device's word log (EditorController.remoteChangeApplied). */
  private async applyRemote(entry: WireEntry, sealed: Uint8Array) {
    const replica = this.replica;
    if (!replica) return;
    if (entry.seq > 0) perfMark('ws:remote', entry.doc);
    const fid = entry.doc.startsWith(FRAGMENT) ? entry.doc.slice(FRAGMENT.length) : null;
    const saved = fid !== null ? this.savedWords.get(fid) : undefined;
    const before = saved !== undefined ? this.wordsOf(fid!) : 0;
    const had = replica.docs.get(entry.doc)?.doc;
    const beforeHeads = had ? A.getHeads(had) : null;
    try {
      await replica.apply(entry, sealed);
    } catch {
      replica.setAside++;
      this.handleEpochs(replica.takeEvents());
      // Sealed with a key this page doesn't have: the project's key may have changed.
      if (entry.seq > 0) void this.checkKey();
      return;
    }
    const events = replica.takeEvents();
    const current = replica.docs.get(entry.doc);
    // Counted towards the snapshot rule only when it went into the document as it now stands.
    if (current && current.epoch === entry.epoch && current.lineage === entry.lineage) this.outbox?.remote(entry.doc, entry.kind, sealed.length);
    if (saved !== undefined) this.savedWords.set(fid!, saved + (this.wordsOf(fid!) - before));
    const handled = this.handleEpochs(events);
    const now = current?.doc;
    if (now && !handled.has(entry.doc) && (!had || A.getHeads(now).join() !== beforeHeads!.join())) {
      for (const fn of this.remoteListeners) fn(entry.doc, beforeHeads, now as A.Doc<unknown>);
    }
  }

  /** A replica wired to this project's outbox (settling before adoptions) and offline store (archives). */
  private async newReplica(restore = true): Promise<Replica> {
    const replica = new Replica(this.key, {
      settle: (doc) => this.outbox?.settle(doc) ?? Promise.resolve(true),
      release: (doc) => this.outbox?.release(doc),
      onArchive: (doc, archive) => this.keepArchive(doc, archive),
    });
    replica.previousKeys = [...this.previousKeys];
    if (restore) {
      for (const a of await this.deps.store.archives(this.id).catch(() => [])) {
        try {
          const doc = A.load<Record<string, unknown>>(await replica.openEntry(a.sealed));
          replica.putArchive(a.doc, { doc, epoch: a.epoch, lineage: a.lineage, synced: a.synced, rebased: a.rebased });
        } catch {
          // another key's, or damaged: the relay's copy carries on without it
        }
      }
    }
    return replica;
  }

  private archiveWrites: Promise<unknown> = Promise.resolve();

  /** Keeps an archived older history (sealed) in the offline store. */
  private keepArchive(doc: string, archive: ArchivedDoc) {
    const bytes = A.save(archive.doc);
    const stored = { doc, epoch: archive.epoch, lineage: archive.lineage, synced: [...archive.synced], rebased: [...archive.rebased] };
    const sealWith = this.deps.seal ?? seal;
    this.archiveWrites = this.archiveWrites.then(async () => this.deps.store.putArchive(this.id, { ...stored, sealed: await sealWith(this.key, bytes) })).catch(() => {});
  }

  /**
   * What the replica did about epochs: adoptions and rebases replace the outbox's older-epoch items with their changes,
   * absorptions are sent, the editor follows, and anything of this device's that couldn't be carried over is noted.
   * Returns the documents whose listeners it told.
   */
  private handleEpochs(events: EpochEvent[]): Set<string> {
    const told = new Set<string>();
    const replica = this.replica;
    if (!replica) return told;
    for (const ev of events) {
      const d = replica.docs.get(ev.doc);
      if (ev.type === 'unplaced') {
        if (ev.own) this.update({ epochNotice: { doc: ev.doc, message: 'This scene’s history was trimmed on another device. Some changes made here are kept on this computer and will be sent as they are.' } });
        continue;
      }
      if (!d) continue;
      if (ev.type === 'adopted' || ev.type === 'rebased') {
        // The baseline is the document as the adoption left it: anything typed since is batched as usual.
        void this.outbox?.replace(ev.doc, { changes: ev.changes, hashes: ev.hashes, epoch: d.epoch, lineage: d.lineage, heads: ev.heads });
        if (ev.type === 'adopted' && ev.touched.length) this.outbox?.changed(ev.touched);
        if (ev.outcome === 'archivedLocalEdits') {
          this.update({ epochNotice: { doc: ev.doc, message: 'This scene’s history was trimmed on another device. Some changes made here couldn’t be merged into it; they’re kept on this computer.' } });
        }
      } else {
        // Absorbed edits are this device's own change (with the record's `absorbedHeads`): batched as usual.
        this.outbox?.changed([ev.doc, ...ev.touched]);
      }
      const fid = ev.doc.startsWith(FRAGMENT) ? ev.doc.slice(FRAGMENT.length) : null;
      const saved = fid !== null ? this.savedWords.get(fid) : undefined;
      if (saved !== undefined) this.typed.delete(fid!); // the words the new history brought aren't this device's typing
      if (fid !== null && saved !== undefined) this.savedWords.set(fid, this.wordsOf(fid));
      for (const fn of this.remoteListeners) fn(ev.doc, ev.beforeHeads, d.doc as A.Doc<unknown>);
      told.add(ev.doc);
    }
    if (told.size) this.scheduleRebuild();
    return told;
  }

  /** Adopts newer epochs that waited for this device's batches to settle, and rebases this device's own older-epoch
   *  batches kept from an earlier session once theirs have. */
  private async settleArchived() {
    const replica = this.replica;
    if (!replica || this.closed) return;
    await replica.adoptPending();
    this.handleEpochs(replica.takeEvents());
    for (const doc of replica.needsRebase()) {
      if (this.outbox && !(await this.outbox.settle(doc))) continue; // not yet: the next connection tries again
      replica.rebaseArchived(doc);
      this.handleEpochs(replica.takeEvents());
    }
  }

  /** Asks the relay again from the cursor (a refused older-epoch snapshot: the newer one is there to adopt). */
  private refetch() {
    const conn = this.conn;
    if (!conn || conn.closed) return;
    this.enqueue(async () => {
      if (this.conn !== conn || conn.closed) return;
      await this.take(conn, await conn.hello(this.cursor));
    });
  }

  /** Batches kept from an earlier session, applied as this device's own (they don't move the cursor). */
  private async applyPending(items: OutboxItem[]) {
    const replica = this.replica;
    if (!replica) return;
    for (const item of items) {
      const entry: WireEntry = { seq: 0, doc: item.doc, kind: item.kind === 'snapshot' ? 'snapshot' : 'batch', epoch: item.epoch, lineage: item.lineage, heads: item.heads, size: item.sealed.length };
      await replica.apply(entry, item.sealed, { own: true }).catch(() => void replica.setAside++);
    }
  }

  /**
   * The relay has this device's item as entry `seq`: its changes are synced, and it's kept in the cache like any entry,
   * so a reload has it. Not queued behind the replica's work: an adoption waiting for this acknowledgement holds that
   * queue (acknowledgements keep their own order).
   */
  private acked(item: OutboxItem, seq: number): Promise<void> {
    this.replica?.acknowledge(item.doc, { epoch: item.epoch, lineage: item.lineage }, item.changes ?? []);
    this.advance(seq);
    const entry: CachedEntry = {
      seq, doc: item.doc, kind: item.kind === 'snapshot' ? 'snapshot' : 'batch', epoch: item.epoch, lineage: item.lineage,
      ...(item.heads ? { heads: item.heads } : {}), bytes: item.sealed,
    };
    const run = this.ackWrites.then(() => this.deps.store.append(this.id, [entry], this.cursor)).then(() => {});
    this.ackWrites = run.catch(() => {});
    return run;
  }

  private ackWrites: Promise<void> = Promise.resolve();

  // MARK: Writing

  /**
   * Applies `ProjectEditor` calls to the live documents; what they change is shown (after a short pause) and batched
   * for the relay. `typed`: the fragments whose text the writer is typing in (their words are logged at each save).
   */
  edit<T>(fn: (editor: ProjectEditor) => T, o: { typed?: string | string[] } = {}): T {
    const replica = this.replica;
    if (!replica) throw new Error('The project isn’t open yet.');
    const typed = o.typed === undefined ? [] : typeof o.typed === 'string' ? [o.typed] : o.typed;
    const fromEditor = o.typed !== undefined;
    for (const id of typed) if (!this.savedWords.has(id)) this.savedWords.set(id, this.wordsOf(id));
    const docs = docsOf(replica);
    const before = new Map(docs);
    // A rewrite outside the editor (Replace) isn't typing: the words it moves are kept out of the next save's log.
    const counted = fromEditor ? null : new Map([...this.savedWords.keys()].map((id) => [id, this.wordsOf(id)]));
    const changed: string[] = [];
    const heads = new Map<string, A.Heads>();
    // A shared project's changes are made with this session's actor, which is recorded in `members` (W9c).
    const attributing = this.attributing(docs.get('project'));
    try {
      const result = fn(editProject(docs, { device: this.deps.device ?? 'web', author: this.deps.author ?? 'Me', ...(attributing ? { actor: this.sessionActor } : {}) }));
      if (attributing) this.recordSelf(docs, before);
      return result;
    } finally {
      // Even if a call threw part-way, every document it changed replaces the one it outdated.
      for (const [name, doc] of docs) {
        const was = before.get(name);
        if (was === doc) continue;
        // Written with this document's actor, the session's (attribution), or both: all of them are this device's.
        this.outbox?.ownActors(was ? [A.getActorId(was), A.getActorId(doc)] : [A.getActorId(doc)]);
        const had = replica.docs.get(name);
        if (was && !fromEditor) heads.set(name, A.getHeads(was));
        if (had) had.doc = doc as A.Doc<Record<string, unknown>>;
        else replica.docs.set(name, { doc: doc as A.Doc<Record<string, unknown>>, epoch: 0, lineage: '', synced: [] });
        changed.push(name);
      }
      if (changed.length) {
        for (const id of typed) if (changed.includes(FRAGMENT + id)) this.typed.add(id);
        if (counted) {
          for (const [id, words] of counted) {
            if (changed.includes(FRAGMENT + id)) this.savedWords.set(id, this.savedWords.get(id)! + (this.wordsOf(id) - words));
          }
        }
        this.outbox?.changed(changed);
        this.scheduleRebuild(fromEditor);
        // The editor follows what changed its text without it, in the same turn (so the next keystroke lands right).
        if (!fromEditor) {
          for (const name of changed) {
            const now = replica.docs.get(name)?.doc as A.Doc<unknown> | undefined;
            if (now) for (const fn of this.localListeners) fn(name, heads.get(name) ?? null, now);
          }
        }
      }
    }
  }

  /** Whether this session's writes are put down to this account (the project is shared: this account is a member, the
   *  owner shared it here, or the project document has `members`). */
  private attributing(project: A.Doc<unknown> | undefined): boolean {
    if (!this.deps.memberId || !project) return false;
    if (this.role !== 'owner' || this.sharedHere) return true;
    if (this.attributingFor.doc !== project) this.attributingFor = { doc: project, value: hasMembers(project) };
    return this.attributingFor.value;
  }

  /** After a shared project's edit: the actors it wrote with (and this device, and the account's name) recorded in
   *  this account's entry of `members`, in a change to the project document beside the edit's, once per session. */
  private recordSelf(docs: Map<string, A.Doc<unknown>>, before: Map<string, A.Doc<unknown>>, force = false) {
    const actors = new Set<string>();
    for (const [name, doc] of docs) if (before.get(name) !== doc) actors.add(A.getActorId(doc));
    if (force) actors.add(this.sessionActor);
    const name = this.deps.author?.trim() ?? '';
    const fresh = [...actors].filter((a) => !this.recordedActors.has(a));
    if (!fresh.length && (name === '' || name === this.recordedName)) return;
    let project = docs.get('project');
    if (!project) return;
    if (A.getActorId(project) !== this.sessionActor && before.get('project') === project) project = A.clone(project, { actor: this.sessionActor });
    const all = [...new Set([...fresh, this.sessionActor])];
    const next = recordMemberIn(project, { id: this.deps.memberId!, name, actors: all, device: this.deps.device, now: new Date() });
    if (next !== project) docs.set('project', next);
    for (const a of all) this.recordedActors.add(a);
    if (name) this.recordedName = name;
  }

  /** The owner shared the project (Share Project…): records the owner in `members` now, so the map exists before anyone
   *  joins and every later write is attributed. */
  recordMember() {
    if (!this.deps.memberId || !this.replica || !this.outbox) return;
    this.sharedHere = true;
    this.edit((editor) => {
      const docs = (editor as ProjectEditor & { docs: Map<string, A.Doc<unknown>> }).docs;
      this.recordSelf(docs, new Map(docs), true);
    });
  }

  /**
   * `edit` for the core's writers that take the documents themselves (reader rounds, reviewers' notes placed as
   * comments: core/review): what they change is shown and batched exactly as `edit`'s changes are.
   */
  editDocuments<T>(fn: (docs: Map<string, A.Doc<unknown>>) => T): T {
    return this.edit((editor) => fn((editor as ProjectEditor & { docs: Map<string, A.Doc<unknown>> }).docs));
  }

  /** Every document with its epoch and lineage, and the cursor the replica has merged: what a rotation re-uploads as
   *  snapshots under the new key (W9a). */
  snapshotSource(): { docs: { name: string; doc: A.Doc<unknown>; epoch: number; lineage: string }[]; cursor: number } {
    const docs = [...(this.replica?.docs ?? [])].map(([name, d]) => ({ name, doc: d.doc as A.Doc<unknown>, epoch: d.epoch, lineage: d.lineage }));
    return { docs, cursor: this.cursor };
  }

  /** Every document as it is now (History compares a checkpoint with them). */
  documents(): Map<string, A.Doc<unknown>> {
    return this.replica ? docsOf(this.replica) : new Map();
  }

  /** Typing not yet saved (logged), in these fragments: words added and cut since each one's last save. `words`: a
   *  fragment's words now (the rebuilt project's, which it has counted already; by default read afresh). */
  unsavedChange(ids: Set<string>, words: (fragmentId: string) => number = (id) => this.wordsOf(id)): { added: number; removed: number } {
    let added = 0, removed = 0;
    for (const id of this.typed) {
      if (!ids.has(id)) continue;
      const saved = this.savedWords.get(id);
      if (saved === undefined) continue;
      const now = words(id);
      added += Math.max(0, now - saved);
      removed += Math.max(0, saved - now);
    }
    return { added, removed };
  }

  /** Calls `fn` with each of this device's changes made outside the editor, after it's applied. */
  onLocal(fn: RemoteListener): () => void {
    this.localListeners.add(fn);
    return () => this.localListeners.delete(fn);
  }

  /** Calls `fn` at each save with a typed-in fragment's words before and after (`wordsSaved`). */
  onWordsSaved(fn: (fragmentId: string, before: number, after: number) => void): () => void {
    this.savedListeners.add(fn);
    return () => this.savedListeners.delete(fn);
  }

  /** Whether this device can write to the project now (its edits have somewhere to wait, and its role writes). */
  get writable(): boolean {
    return !!this.outbox && !!this.replica && this.role !== 'commenter';
  }

  /** Whether this device can add comments and suggestions now (every role can). */
  get canComment(): boolean {
    return !!this.outbox && !!this.replica;
  }

  // MARK: Key rotation (W9a)

  private keyCheck: Promise<void> | null = null;
  private lastKeyCheck = 0;

  /** Asks for the project's key now (at most every few seconds) and takes it if it changed. */
  checkKey(): Promise<void> {
    if (!this.deps.fetchKey || this.closed) return Promise.resolve();
    if (this.keyCheck) return this.keyCheck;
    if (Date.now() - this.lastKeyCheck < 3000) return Promise.resolve();
    this.lastKeyCheck = Date.now();
    this.keyCheck = (async () => {
      try {
        const key = await this.deps.fetchKey!();
        if (key && !sameBytes(key, this.key)) await this.rekey(key);
      } catch {
        // tried again at the next refusal
      } finally {
        this.keyCheck = null;
      }
    })();
    return this.keyCheck;
  }

  /**
   * The project's key changed: everything kept here is re-sealed with the new one (the outbox's items, which then go
   * again; the cached entries), the old key is kept in memory for what it sealed, and the replica is rebuilt from the
   * cache so entries that arrived before the key did are read now.
   */
  async rekey(key: Uint8Array): Promise<void> {
    if (sameBytes(key, this.key)) return;
    const previous = [this.key, ...this.previousKeys.filter((k) => !sameBytes(k, this.key))];
    this.key = key;
    this.previousKeys = previous;
    if (this.replica) Object.assign(this.replica, { key, previousKeys: previous });
    const sealWith = this.deps.seal ?? seal;
    const openAny = async (bytes: Uint8Array) => {
      for (const k of [key, ...previous]) {
        try {
          return await openSealed(k, bytes);
        } catch {
          // another key's
        }
      }
      return null;
    };
    await this.outbox?.rekey(key, previous);
    const done = new Promise<void>((resolve) => {
      this.enqueue(async () => {
        try {
          await this.deps.store.reseal(this.id, async (b) => {
            // Sealed with the new key already: kept as it is.
            try {
              await openSealed(key, b);
              return null;
            } catch {
              return openAny(b);
            }
          }, (b) => sealWith(key, b));
          // What the cache holds, read with every key now, merged into the documents as they are (so typing not yet
          // batched, and this session's actor, stay).
          const replica = this.replica;
          if (replica) {
            replica.key = key;
            replica.previousKeys = previous;
            const fresh = await replicaFrom(key, await this.deps.store.entries(this.id), Object.assign(new Replica(key), { previousKeys: previous }));
            fresh.settleEpochs();
            for (const [name, f] of fresh.docs) {
              const have = replica.docs.get(name);
              const had = have ? A.getHeads(have.doc) : null;
              if (!have) replica.docs.set(name, f);
              else if (have.epoch === f.epoch && have.lineage === f.lineage) {
                have.doc = A.merge(have.doc, f.doc);
                have.synced = [...new Set([...have.synced, ...f.synced])].sort();
              } else continue;
              const now = replica.docs.get(name)!.doc;
              if (!had || A.getHeads(now).join() !== had.join()) for (const fn of this.remoteListeners) fn(name, had, now as A.Doc<unknown>);
            }
          }
          this.rebuild();
        } finally {
          resolve();
        }
      });
    });
    await done;
    // What the relay sent while this page couldn't read it is in the cache now; anything after comes as usual.
    this.refetch();
  }

  /** A fragment's document as it is now. */
  doc(name: string): A.Doc<unknown> | undefined {
    return this.replica?.docs.get(name)?.doc as A.Doc<unknown> | undefined;
  }

  /** A fragment's text as it is now (the editor compares with it when another device's change arrives). */
  text(fragmentId: string): string {
    const d = this.replica?.docs.get(FRAGMENT + fragmentId)?.doc;
    return d ? (new SwiftReader(d).string(ROOT, 'text') ?? '') : '';
  }

  /** Calls `fn` with each presence frame another member's device sends (W9b). */
  onPresenceFrame(fn: (header: PresenceHeader, body: Uint8Array) => void): () => void {
    this.presenceListeners.add(fn);
    return () => this.presenceListeners.delete(fn);
  }

  /** Sends this device's presence (sealed already), or `gone`; false when the socket isn't open (W9b). */
  sendPresence(o: { ttl: number; sealed: Uint8Array } | { gone: true }): boolean {
    const conn = this.conn as Partial<Pick<SyncConnection, 'sendPresence'>> | null;
    return !!conn?.sendPresence?.(o);
  }

  /** Calls `fn` with each document another device changed, after it's applied. */
  onRemote(fn: RemoteListener): () => void {
    this.remoteListeners.add(fn);
    return () => this.remoteListeners.delete(fn);
  }

  /** Batches and stores what's typed now (the page is closing or hidden). */
  flush(): Promise<void> {
    return this.outbox?.flush() ?? Promise.resolve();
  }

  private noteAttachments(entries: CachedEntry[]) {
    for (const e of entries) {
      if (e.doc.startsWith('attachment.')) this.attachmentSeqs.set(e.doc, Math.max(this.attachmentSeqs.get(e.doc) ?? 0, e.seq));
    }
  }

  /** Whether an attachment's bytes are on this device (cached from the relay's `attachment.<hmac>` snapshot). */
  async hasAttachment(sha256: string): Promise<boolean> {
    return this.openedAttachments.has(sha256) || this.attachmentSeqs.has(await attachmentDoc(this.key, fromHex(sha256)));
  }

  /** An attachment's bytes (`DocumentStore.attachmentData`): the relay's snapshot this device has cached, opened with the
   *  project key (or one it had before a rotation) and checked against its hash; null when it isn't here or doesn't
   *  match. Opened files are kept in memory, newest last, up to `ATTACHMENT_CACHE_BYTES`. */
  async attachment(sha256: string): Promise<Uint8Array | null> {
    const hit = this.openedAttachments.get(sha256);
    if (hit) return hit;
    const name = await attachmentDoc(this.key, fromHex(sha256));
    if (!this.attachmentSeqs.has(name)) return null;
    const entries = await this.deps.store.entries(this.id);
    const entry = entries.filter((e) => e.doc === name).sort((a, b) => b.seq - a.seq)[0];
    if (!entry) return null;
    let bytes: Uint8Array;
    try {
      bytes = this.replica ? await this.replica.openEntry(entry.bytes) : await openSealed(this.key, entry.bytes);
    } catch {
      return null;
    }
    if (sha256Hex(bytes) !== sha256.toLowerCase()) return null;
    this.openedAttachments.set(sha256, bytes);
    this.openedAttachmentBytes += bytes.length;
    for (const [k, v] of this.openedAttachments) {
      if (this.openedAttachmentBytes <= ATTACHMENT_CACHE_BYTES || k === sha256) break;
      this.openedAttachments.delete(k);
      this.openedAttachmentBytes -= v.length;
    }
    return bytes;
  }

  private wordsOf(fragmentId: string): number {
    const d = this.replica?.docs.get(FRAGMENT + fragmentId)?.doc;
    if (!d) return 0;
    const r = new SwiftReader(d);
    return wordCount(r.string(ROOT, 'text') ?? '', (r.string(ROOT, 'kind') ?? 'prose') as FragmentKind);
  }

  /** The save's word log (EditorController.performSave): what each fragment typed in gained and lost since the last. */
  private logWords(names: string[]) {
    for (const name of names) {
      if (!name.startsWith(FRAGMENT)) continue;
      const fid = name.slice(FRAGMENT.length);
      if (!this.typed.delete(fid)) continue;
      const words = this.wordsOf(fid);
      const before = this.savedWords.get(fid) ?? words;
      this.edit((e) => e.recordWords(fid, Math.max(0, words - before), Math.max(0, before - words)), { typed: [] });
      this.savedWords.set(fid, words);
      for (const fn of this.savedListeners) fn(fid, before, words);
    }
  }

  /**
   * The project is rebuilt 40 ms after a change — except typing, which the editor already shows: then it waits for a
   * 250 ms pause (at most a second while the typing goes on), so the pages redraw a few times a second, not per key.
   */
  private scheduleRebuild(typing = false) {
    const now = performance.now();
    const at = typing ? Math.min(now + TYPING_PAUSE_MS, (this.rebuildDue ?? now) + TYPING_MAX_MS) : now + 40;
    // Typing pushes its own rebuild on (a pause); an earlier one for anything else stays.
    if (this.rebuildTimer && this.rebuildAt <= at && !(typing && this.rebuildForTyping)) return;
    if (this.rebuildTimer) clearTimeout(this.rebuildTimer);
    if (this.rebuildDue === null) this.rebuildDue = now;
    this.rebuildAt = at;
    this.rebuildForTyping = typing;
    this.rebuildTimer = setTimeout(() => {
      this.rebuildTimer = null;
      this.rebuildDue = null;
      this.rebuild();
    }, Math.max(0, at - now));
  }
  private rebuildForTyping = false;
  /** When the first change not yet shown was made (typing's rebuild waits at most a second from it), and when the
   *  rebuild is due. */
  private rebuildDue: number | null = null;
  private rebuildAt = 0;

  private rebuild() {
    const replica = this.replica;
    if (!replica || !replica.docs.has('project')) return;
    const t0 = performance.now();
    try {
      // What the last project read from documents that haven't changed since is reused (one scene typed in is
      // read again, not the whole book).
      const project = this.deps.build ? this.deps.build(docsOf(replica)) : openDocs(docsOf(replica), this.state.project);
      perfMeasure('ws:rebuild', t0);
      this.update({ status: 'ready', project, version: this.state.version + 1, error: null });
      const info = { title: project.title, type: project.type, series: project.series, words: project.manuscriptWords() };
      const key = JSON.stringify(info);
      if (key !== this.lastInfo) {
        this.lastInfo = key;
        this.infoSaved = this.deps.store.putInfo(this.id, { ...info, wordsAt: this.cursor });
      }
    } catch (e) {
      this.update({ status: this.state.project ? 'ready' : 'failed', error: e instanceof Error ? e.message : String(e) });
    }
  }

  /** Many small cached entries (a long-lived project) become one snapshot per document, in line with other work. */
  private compactIfLarge(count: number) {
    if (count <= COMPACT_AFTER || !this.deps.store.persistent) return;
    this.enqueue(async () => {
      if (!this.replica || this.closed) return;
      const entries = await this.deps.store.entries(this.id);
      const compact = await compactEntries(this.replica, this.key, entries, this.deps.seal ?? seal);
      if (compact.length < entries.length) await this.deps.store.replace(this.id, compact, this.cursor);
    });
  }
}
